import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import type { TenantPublic } from '@repo/shared';
import { api } from '../lib/api';

const TenantContext = createContext<TenantPublic | null>(null);

// Public branding is kept per host so the installed PWA can start offline (it is refetched when online).
const SAVED_KEY = `sol-tenant:${location.hostname}`;
function savedTenant(): TenantPublic | undefined {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    return raw ? (JSON.parse(raw) as TenantPublic) : undefined;
  } catch {
    return undefined;
  }
}

export const tenantQuery = {
  queryKey: ['tenant-public'],
  queryFn: async () => {
    const t = await api<TenantPublic>('/tenant/public');
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(t));
    } catch {
      // Storage disabled: online only.
    }
    return t;
  },
  initialData: savedTenant,
  initialDataUpdatedAt: 0,
  staleTime: 5 * 60_000,
} as const;

/** Loads public branding for the current host (tenant or platform console) before anything else renders. */
export function TenantProvider({
  children,
  fallback,
}: {
  children: (tenant: TenantPublic) => ReactNode;
  fallback: (error: unknown) => ReactNode;
}) {
  const q = useQuery(tenantQuery);

  useEffect(() => {
    if (!q.data) return;
    try {
      localStorage.setItem(
        `sol-brand:${location.hostname}`,
        JSON.stringify({ primary: q.data.primaryColor }),
      );
    } catch {
      // ignore
    }
    document.title =
      q.data.kind === 'platform' ? q.data.productName : `${q.data.shortName} · ${q.data.productName}`;
    document.documentElement.style.setProperty('--p-base', q.data.primaryColor);
  }, [q.data]);

  if (q.error) return <>{fallback(q.error)}</>;
  if (!q.data) return null;
  return <TenantContext.Provider value={q.data}>{children(q.data)}</TenantContext.Provider>;
}

export function useTenant(): TenantPublic {
  const t = useContext(TenantContext);
  if (!t) throw new Error('useTenant outside TenantProvider');
  return t;
}
